import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Order, Coupon } from '@/lib/mock-data'
import { MOCK_ORDERS, MOCK_COUPONS } from '@/lib/mock-data'
import { generateOrderNo } from '@/lib/utils'

interface OrderStore {
  orders: Order[]
  coupons: Coupon[]
  selectedCouponId: string | null
  addOrder: (order: Omit<Order, 'orderNo' | 'createdAt'>) => Order
  useCoupon: (couponId: string) => void
  selectCoupon: (couponId: string | null) => void
  restoreCoupon: (couponId: string) => void
  getAvailableCoupons: (totalPrice: number) => Coupon[]
  getSelectedCoupon: () => Coupon | null
}

export const useOrderStore = create<OrderStore>()(
  persist(
    (set, get) => ({
      orders: MOCK_ORDERS,
      coupons: MOCK_COUPONS,
      selectedCouponId: null,

      addOrder: (orderData) => {
        const orderNo = generateOrderNo()
        const order: Order = {
          ...orderData,
          orderNo,
          createdAt: new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-'),
        }
        set((state) => ({
          orders: [order, ...state.orders],
          selectedCouponId: null,
        }))
        return order
      },

      useCoupon: (couponId) => {
        set((state) => ({
          coupons: state.coupons.map(c =>
            c.id === couponId ? { ...c, used: true } : c
          ),
          selectedCouponId: null,
        }))
      },

      selectCoupon: (couponId) => {
        set({ selectedCouponId: couponId })
      },

      restoreCoupon: (couponId) => {
        set((state) => ({
          coupons: state.coupons.map(c =>
            c.id === couponId ? { ...c, used: false } : c
          ),
        }))
      },

      getAvailableCoupons: (totalPrice) => {
        return get().coupons.filter(c =>
          !c.used && totalPrice >= c.threshold
        )
      },

      getSelectedCoupon: () => {
        const state = get()
        if (!state.selectedCouponId) return null
        return state.coupons.find(c => c.id === state.selectedCouponId) || null
      },
    }),
    {
      name: 'cake-shop-orders',
    }
  )
)
